const getRuntimeEnv = (name) => {
  try {
    if (typeof window === "undefined") return undefined;
    return window.__ENV?.[name];
  } catch {
    return undefined;
  }
};

const getEnv = (name, fallback) => {
  const runtimeValue = getRuntimeEnv(name);
  if (runtimeValue !== undefined && runtimeValue !== "") return runtimeValue;

  const buildTimeValue = process.env[name];
  if (buildTimeValue !== undefined && buildTimeValue !== "") return buildTimeValue;

  return fallback;
};

const isBrowser = typeof window !== "undefined";

/** URLs that must not be called from the browser (mixed content / unreachable). */
const isUnsafeWhatsAppUrlForBrowser = (url) => {
  const u = String(url || "").trim().toLowerCase();
  if (!u) return true;
  if (u.includes("host.docker.internal")) return true;
  if (isBrowser && window.location?.protocol === "https:" && u.startsWith("http://")) {
    return true;
  }
  return false;
};

const isCrossOriginUrlForBrowser = (url) => {
  if (!isBrowser) return false;
  const trimmed = String(url || "").trim();
  if (!trimmed) return false;
  try {
    const targetOrigin = new URL(trimmed, window.location.href).origin;
    return targetOrigin !== window.location.origin;
  } catch {
    return true;
  }
};

/** URLs that must not be called from the browser (mixed content / unreachable / CORS). */
const isUnsafeBillingUrlForBrowser = (url) => {
  const u = String(url || "").trim().toLowerCase();
  if (!u) return true;
  if (u.includes("host.docker.internal")) return true;
  if (isBrowser && window.location?.protocol === "https:" && u.startsWith("http://")) {
    return true;
  }
  if (isCrossOriginUrlForBrowser(url)) return true;
  return false;
};

const pickFirstWhatsAppEnv = (keys) => {
  for (const key of keys) {
    const runtime = getRuntimeEnv(key);
    if (runtime !== undefined && runtime !== "" && !isUnsafeWhatsAppUrlForBrowser(runtime)) {
      return runtime;
    }
  }
  for (const key of keys) {
    const built = process.env[key];
    if (built && !isUnsafeWhatsAppUrlForBrowser(built)) return built;
  }
  return "";
};

const pickFirstWhatsAppKey = (keys) => {
  for (const key of keys) {
    const runtime = getRuntimeEnv(key);
    if (runtime !== undefined && runtime !== "") return runtime;
  }
  for (const key of keys) {
    if (process.env[key]) return process.env[key];
  }
  return "";
};

const WHATSAPP_URL_KEYS = [
  "FRONTEND_WHATSAPP_API_BASE_URL",
  "WHATSAPP_API_BASE_URL",
  "REACT_APP_WHATSAPP_API_BASE_URL",
];

const WHATSAPP_KEY_KEYS = [
  "FRONTEND_WHATSAPP_API_KEY",
  "WHATSAPP_API_KEY",
  "REACT_APP_WHATSAPP_API_KEY",
];

const adminApiBaseUrl = String(
  getEnv("FRONTEND_API_BASE_URL", getEnv("REACT_APP_API_URL", ""))
).replace(/\/+$/, "");

const directWhatsAppBaseUrl = pickFirstWhatsAppEnv(WHATSAPP_URL_KEYS);

const shouldUseWhatsAppProxy =
  isBrowser &&
  Boolean(adminApiBaseUrl) &&
  (isUnsafeWhatsAppUrlForBrowser(directWhatsAppBaseUrl) ||
    isUnsafeWhatsAppUrlForBrowser(getRuntimeEnv("REACT_APP_WHATSAPP_API_BASE_URL")) ||
    isUnsafeWhatsAppUrlForBrowser(process.env.REACT_APP_WHATSAPP_API_BASE_URL));

const resolvedWhatsAppBaseUrl = shouldUseWhatsAppProxy
  ? `${adminApiBaseUrl}/whatsapp/proxy`
  : directWhatsAppBaseUrl;

const resolvedWhatsAppApiKey = shouldUseWhatsAppProxy
  ? getEnv("FRONTEND_API_KEY", getEnv("REACT_APP_API_KEY", ""))
  : pickFirstWhatsAppKey(WHATSAPP_KEY_KEYS);

const directBillingBaseUrl = String(
  getEnv("FRONTEND_BILLING_API_BASE_URL", getEnv("REACT_APP_BILLING_API_BASE_URL", ""))
).replace(/\/+$/, "");

const shouldUseBillingProxy =
  isBrowser &&
  Boolean(adminApiBaseUrl) &&
  isUnsafeBillingUrlForBrowser(directBillingBaseUrl);

const resolvedBillingBaseUrl = shouldUseBillingProxy
  ? `${adminApiBaseUrl}/billing/proxy`
  : directBillingBaseUrl;

const resolvedBillingApiKey = shouldUseBillingProxy
  ? getEnv("FRONTEND_API_KEY", getEnv("REACT_APP_API_KEY", ""))
  : getEnv("FRONTEND_BILLING_API_KEY", getEnv("REACT_APP_BILLING_API_KEY", ""));

// API Configuration
export const API_CONFIG = {
  // In development, docker-compose injects these as `REACT_APP_*` build-time env vars.
  // In production, we fall back to the existing hardcoded values.
  // Prefer runtime `window.__ENV` (docker-adminzone/env.js), then build-time env, then defaults.
  // Do not add a UISP/UNMS base URL — UISP is server-side only; the SPA talks to Flask only.
  baseUrl: getEnv("FRONTEND_API_BASE_URL", getEnv("REACT_APP_API_URL", "https://api.nanotech.net.za")),
  apiKey: getEnv("FRONTEND_API_KEY", getEnv("REACT_APP_API_KEY", "UbxX4mB8AHU3Rbq0QqLbAKeWk5Y33r")),
  setupUsername: getEnv("FRONTEND_SETUP_USERNAME", getEnv("REACT_APP_SETUP_USERNAME", "admin")),
  setupPassword: getEnv("FRONTEND_SETUP_PASSWORD", getEnv("REACT_APP_SETUP_PASSWORD", "admin123")),
  enableOfflineSupport:
    String(getEnv("FRONTEND_ENABLE_OFFLINE_SUPPORT", getEnv("REACT_APP_ENABLE_OFFLINE_SUPPORT", "false")))
      .toLowerCase() === "true",
  enableServiceWorker:
    String(getEnv("FRONTEND_ENABLE_SERVICE_WORKER", getEnv("REACT_APP_ENABLE_SERVICE_WORKER", "false")))
      .toLowerCase() === "true",

  // Billing API — direct in dev when same-origin; otherwise Admin API /billing/proxy.
  billingApi: {
    baseUrl: resolvedBillingBaseUrl,
    apiKey: resolvedBillingApiKey,
    useProxy: shouldUseBillingProxy,
  },

  // WhatsApp API — direct to WHATSAPP_API_BASE_URL in dev; HTTPS production uses Admin API /whatsapp/proxy.
  whatsappApi: {
    baseUrl: resolvedWhatsAppBaseUrl,
    apiKey: resolvedWhatsAppApiKey,
    useProxy: shouldUseWhatsAppProxy,
    /** Normalized digits-only peer id to hide from inbox (business / helpdesk line). */
    helpdeskWaId: getEnv(
      "REACT_APP_WHATSAPP_HELPDESK_WA_ID",
      getEnv("WHATSAPP_HELPDESK_WA_ID", getEnv("FRONTEND_WHATSAPP_HELPDESK_WA_ID", "27135180008"))
    ),
    /** Shown as sender on outbound inbox previews when status API has no push name. */
    businessProfileName: getEnv(
      "REACT_APP_WHATSAPP_BUSINESS_PROFILE_NAME",
      getEnv(
        "WHATSAPP_BUSINESS_PROFILE_NAME",
        getEnv("FRONTEND_WHATSAPP_BUSINESS_PROFILE_NAME", "NanoTech NetComs")
      )
    ),
  },

  // Some parts of the app reference `isProduction` for logging/sync behavior.
  isProduction:
    String(getEnv("FRONTEND_IS_PRODUCTION", getEnv("REACT_APP_IS_PRODUCTION", "true"))).toLowerCase() === "true",
};

// Environment Configuration
export const ENV_CONFIG = {
  environment: getEnv("FRONTEND_ENVIRONMENT", getEnv("REACT_APP_ENVIRONMENT", "development")),
  isProduction:
    String(getEnv("FRONTEND_IS_PRODUCTION", getEnv("REACT_APP_IS_PRODUCTION", "true"))).toLowerCase() === "true",
};
